/**
 * Plan Context
 * Manages plan tier state, usage tracking, and feature access checks.
 *
 * Depends on AuthContext, only fetches billing data when authenticated.
 * Defaults to 'free' tier on error or while loading (safe default).
 */

import React, { createContext, useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { billingService } from '../services/billing.service'
import { planService } from '../services/plan.service'
import { FEATURE_REGISTRY } from '../config/featureRegistry'
import {
  PlanState,
  PlanFeature,
  PlanUsage,
  FeatureAccess,
  FEATURE_USAGE_KEYS,
} from '../types/plan.types'
import { PlanTier, SubscriptionStatus } from '../types/billing.types'

/** How often to refresh usage data (ms) */
const USAGE_REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

export const PlanContext = createContext<PlanState | undefined>(undefined)

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth()

  const [tier, setTier] = useState<PlanTier>('free')
  const [status, setStatus] = useState<SubscriptionStatus>('active')
  const [isLoading, setIsLoading] = useState(true)
  const [usage, setUsage] = useState<PlanUsage>({})

  const hasInitialized = useRef(false)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  /**
   * Determine the effective tier for feature checks.
   * Canceled subscriptions are treated as free tier.
   */
  const getEffectiveTier = useCallback((rawTier: PlanTier, rawStatus: SubscriptionStatus): PlanTier => {
    if (rawStatus === 'canceled') return 'free'
    return rawTier
  }, [])

  /**
   * Check if a specific feature is accessible for the current plan.
   * Combines static registry entitlements with live usage data.
   */
  const checkFeature = useCallback(
    (feature: PlanFeature): FeatureAccess => {
      const effectiveTier = getEffectiveTier(tier, status)
      const entitlement = FEATURE_REGISTRY[feature]?.[effectiveTier]

      if (!entitlement || !entitlement.enabled) {
        return { allowed: false, gateType: entitlement?.gateType ?? 'boolean' }
      }

      // Boolean gate, simple enabled/disabled
      if (entitlement.gateType === 'boolean') {
        return { allowed: true, gateType: 'boolean' }
      }

      // Quota gate, check usage against limit
      if (entitlement.gateType === 'quota') {
        // No limit means unlimited
        if (entitlement.limit === undefined) {
          return { allowed: true, gateType: 'quota' }
        }

        const usageKey = FEATURE_USAGE_KEYS[feature]
        const currentUsage = usageKey ? (usage[usageKey] ?? 0) : 0
        const remaining = Math.max(0, entitlement.limit - currentUsage)
        const exceeded = currentUsage >= entitlement.limit

        return {
          allowed: !exceeded,
          gateType: 'quota',
          limit: entitlement.limit,
          usage: currentUsage,
          remaining,
          exceeded,
        }
      }

      return { allowed: true, gateType: 'boolean' }
    },
    [tier, status, usage, getEffectiveTier]
  )

  /**
   * Fetch plan tier and usage data from backend
   */
  const fetchPlanData = useCallback(async () => {
    try {
      const [billingSettings, usageData] = await Promise.all([
        billingService.getBillingSettings(),
        planService.getUsage(),
      ])

      setTier(billingSettings.plan)
      setStatus(billingSettings.status)
      setUsage(usageData)
    } catch (err) {
      console.warn('[PlanContext] Failed to fetch plan data, defaulting to free', err)
      setTier('free')
      setStatus('active')
      setUsage({})
    }
  }, [])

  /**
   * Public refresh function, forces re-fetch of plan and usage data
   */
  const refreshPlan = useCallback(async () => {
    await fetchPlanData()
  }, [fetchPlanData])

  /**
   * Initialize plan data when user authenticates
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset to defaults when logged out
      setTier('free')
      setStatus('active')
      setUsage({})
      setIsLoading(false)
      hasInitialized.current = false
      return
    }

    const initialize = async () => {
      setIsLoading(true)
      await fetchPlanData()
      setIsLoading(false)
      hasInitialized.current = true
    }

    initialize()
  }, [isAuthenticated, fetchPlanData])

  /**
   * Periodic usage refresh (every 5 minutes while authenticated)
   */
  useEffect(() => {
    if (!isAuthenticated || !hasInitialized.current) return

    refreshTimerRef.current = setInterval(async () => {
      try {
        const usageData = await planService.getUsage()
        setUsage(usageData)
      } catch {
        // Silently fail, stale usage is acceptable
      }
    }, USAGE_REFRESH_INTERVAL)

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [isAuthenticated])

  /**
   * Refresh usage on window focus (catch up after tab was backgrounded)
   */
  useEffect(() => {
    if (!isAuthenticated) return

    const handleFocus = async () => {
      if (!hasInitialized.current) return
      try {
        const usageData = await planService.getUsage()
        setUsage(usageData)
      } catch {
        // Silently fail
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [isAuthenticated])

  /**
   * Listen for backend 403 plan-limit events dispatched by the API interceptor
   */
  useEffect(() => {
    const handlePlanLimit = () => {
      // Re-fetch to get updated usage/tier state
      refreshPlan()
    }

    window.addEventListener('plan:limit-reached', handlePlanLimit)
    return () => window.removeEventListener('plan:limit-reached', handlePlanLimit)
  }, [refreshPlan])

  return (
    <PlanContext.Provider
      value={{
        tier,
        status,
        isLoading,
        usage,
        checkFeature,
        refreshPlan,
      }}>
      {children}
    </PlanContext.Provider>
  )
}
